import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  type AccessGrant,
  RoleGrantsField,
  roleAccessBody,
  TeamGrantsField,
  teamAccessBody,
} from '@/entities/collab';
import { type Database, useCreateDatabase } from '@/entities/database';
import type { Workspace } from '@/entities/workspace';
import { FromTemplateButton } from '@/features/template-manage';
import { FormError } from '@/shared/forms';
import { Button, Combobox, Dialog, DialogActions, TextAreaField, TextField } from '@/shared/ui';

import styles from './DatabaseCatalog.module.css';

export const DATABASE_NAME_MAX = 100;
const DESCRIPTION_MAX = 5000;

interface Props {
  /** Workspaces the viewer may create a database in (`workspace.create_database`). */
  workspaces: readonly Workspace[];
  /** Preselected workspace (the one the dialog was opened from). */
  workspaceId?: string;
  /** File the new database in this section of that workspace in the same request (UD-STRUCT). */
  sectionId?: string | null | undefined;
  /** Inside this group of that workspace (it decides the section). */
  groupId?: string | null | undefined;
  onCreated: (database: Database) => void;
  onClose: () => void;
}

/** "New database": name, optional description, the workspace and the confidential mode. */
export function CreateDatabaseDialog({
  workspaces,
  workspaceId,
  sectionId,
  groupId,
  onCreated,
  onClose,
}: Props) {
  const { t } = useTranslation();
  const create = useCreateDatabase();
  const [target, setTarget] = useState(workspaceId ?? workspaces[0]?.id ?? '');
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [confidential, setConfidential] = useState(false);
  const [teamAccess, setTeamAccess] = useState<AccessGrant[]>([]);
  const [roleAccess, setRoleAccess] = useState<AccessGrant[]>([]);
  const [touched, setTouched] = useState(false);
  // Teams and roles belong to ONE workspace; only a team workspace has them.
  const isTeam = workspaces.find((w) => w.id === target)?.workspace_type === 'team';
  const sharing = isTeam && !confidential;
  const nameError = touched && name.trim() === '' ? t('validation.REQUIRED') : undefined;

  const submit = () => {
    setTouched(true);
    if (name.trim() === '' || target === '') return;
    create.mutate(
      {
        workspaceId: target,
        name: name.trim(),
        ...(description.trim() && { description: description.trim() }),
        is_confidential: confidential,
        ...(sharing && teamAccess.length > 0 && { team_access: teamAccessBody(teamAccess) }),
        ...(sharing && roleAccess.length > 0 && { role_access: roleAccessBody(roleAccess) }),
        // only for the workspace the dialog was opened from (a section belongs to one workspace)
        ...(sectionId && target === workspaceId && { section_id: sectionId }),
        ...(groupId && target === workspaceId && { group_id: groupId }),
      },
      { onSuccess: onCreated },
    );
  };

  return (
    <Dialog title={t('databases.createTitle')} onClose={onClose}>
      <FromTemplateButton<Database>
        kind="database"
        workspaceId={target}
        sectionId={target === workspaceId ? sectionId : null}
        groupId={target === workspaceId ? groupId : null}
        onCreated={onCreated}
      />
      <form
        className={styles.form}
        onSubmit={(event) => {
          event.preventDefault();
          submit();
        }}
      >
        <TextField
          label={t('databases.name')}
          value={name}
          maxLength={DATABASE_NAME_MAX}
          autoFocus
          error={nameError}
          onChange={(event) => {
            setName(event.target.value);
          }}
        />
        <TextAreaField
          label={t('databases.description')}
          value={description}
          maxLength={DESCRIPTION_MAX}
          rows={3}
          onChange={(event) => {
            setDescription(event.target.value);
          }}
        />
        {workspaces.length > 1 && (
          <Combobox
            label={t('databases.workspace')}
            value={target}
            options={workspaces.map((w) => ({
              value: w.id,
              label: w.workspace_type === 'personal' ? t('home.personalWorkspace') : w.name,
            }))}
            onChange={(next) => {
              setTarget(next);
              setTeamAccess([]); // another workspace has other teams and roles
              setRoleAccess([]);
            }}
          />
        )}
        <label className={styles.toggle}>
          <input
            type="checkbox"
            checked={confidential}
            onChange={(event) => {
              setConfidential(event.target.checked);
            }}
          />
          <span>
            <strong>{t('databases.confidential')}</strong>
            <span className={styles.hint}>{t('databases.confidentialHint')}</span>
          </span>
        </label>
        {sharing && (
          <>
            <TeamGrantsField
              workspaceId={target}
              enabled={sharing}
              value={teamAccess}
              onChange={setTeamAccess}
              texts={{
                title: t('databases.createAccess.teamsTitle'),
                hint: t('databases.createAccess.teamsHint'),
                add: t('databases.createAccess.addTeam'),
                remove: (who) => t('databases.createAccess.removeGrant', { name: who }),
              }}
            />
            <RoleGrantsField
              workspaceId={target}
              enabled={sharing}
              value={roleAccess}
              onChange={setRoleAccess}
              texts={{
                title: t('databases.createAccess.rolesTitle'),
                hint: t('databases.createAccess.rolesHint'),
                add: t('databases.createAccess.addRole'),
                remove: (who) => t('databases.createAccess.removeGrant', { name: who }),
              }}
            />
          </>
        )}
        {isTeam && confidential && (
          <p className={styles.hint}>{t('databases.createAccess.confidentialNoRules')}</p>
        )}
        <FormError error={create.error} />
        <DialogActions>
          <Button type="button" variant="ghost" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button type="submit" variant="primary" disabled={create.isPending}>
            {t('databases.create')}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
